<template>
  <AdminLayout>
    <AdminPreloader :loading="loading" message="Loading users..." />
    <div class="admin-users">
      <h2 class="mb-4">Manage Users</h2>

      <div class="filters-container mb-4">
        <div class="row g-3">
          <div class="col-md-6">
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input type="text" v-model="searchQuery" class="form-control" placeholder="Search by name or email...">
            </div>
          </div>
        </div>
      </div>

      <div class="orders-table-container">
        <table class="modern-orders-table">
          <thead>
            <tr>
              <th>User ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Email Status</th>
              <th>Actions</th>
            </tr>

          </thead>
          <tbody>
            <tr v-for="user in paginatedUsers" :key="user.id" class="order-row">
              <td class="order-id">#{{ user.id }}</td>
              <td>
                <div class="customer-info">
                  <div class="customer-avatar">
                    {{ user.name ? user.name.charAt(0).toUpperCase() : 'U' }}
                  </div>
                  <span class="customer-name">{{ user.name || 'N/A' }}</span>
                </div>
              </td>
              <td>{{ user.email }}</td>
              <td>
                <span class="status-badge" :class="user.is_admin ? 'role-admin' : 'role-user'">
                  {{ user.is_admin ? 'Admin' : 'User' }}
                </span>
              </td>
              <td>
                <span class="status-badge" :class="user.email_verified_at ? 'verified' : 'unverified'">
                  {{ user.email_verified_at ? 'Verified' : 'Unverified' }}
                </span>
              </td>
              <td>
                <div class="btn-group btn-group-sm">
                  <!-- <button
                    v-if="!user.is_admin"
                    class="btn btn-success"
                    @click="toggleAdmin(user)"
                    :disabled="submitting"
                  >
                    Make Admin
                  </button>
                  <button
                    v-else
                    class="btn btn-warning"
                    @click="toggleAdmin(user)"
                    :disabled="submitting"
                  >
                    Remove Admin
                  </button> -->
                  <button class="action-btn btn-danger" @click="deleteUser(user.id)" :disabled="submitting">
                    <i class="fas fa-trash"></i>
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-container" v-if="totalPages > 1">
        <div class="pagination-info">
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalUsers) }}
          of {{ totalUsers }} entries
        </div>
        <div class="pagination-controls">
          <button class="pagination-btn" :disabled="currentPage === 1" @click="currentPage--">
            <i class="fas fa-chevron-left"></i> Previous
          </button>
          <button v-for="page in visiblePages" :key="page" class="pagination-btn"
            :class="{ active: page === currentPage }" @click="currentPage = page">
            {{ page }}
          </button>
          <button class="pagination-btn" :disabled="currentPage === totalPages" @click="currentPage++">
            Next <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import AdminLayout from '../components/AdminLayout.vue'
import AdminPreloader from '../components/AdminPreloader.vue'
import { useApiStore } from '../stores/api'

export default {
  name: 'AdminUsers',
  components: {
    AdminLayout,
    AdminPreloader
  },
  setup() {
    const users = ref([])
    const loading = ref(true)
    const submitting = ref(false)
    const searchQuery = ref('')
    const apiStore = useApiStore()

    // Pagination state
    const currentPage = ref(1)
    const itemsPerPage = ref(10)

    const filteredUsers = computed(() => {
      let result = users.value

      // Apply search query
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(user =>
          (user.name && user.name.toLowerCase().includes(query)) ||
          (user.email && user.email.toLowerCase().includes(query)) ||
          user.id.toString().includes(query)
        )
      }

      return result
    })

    const totalUsers = computed(() => filteredUsers.value.length)
    const totalPages = computed(() => Math.ceil(totalUsers.value / itemsPerPage.value))

    const paginatedUsers = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value
      const end = start + itemsPerPage.value
      return filteredUsers.value.slice(start, end)
    })

    const visiblePages = computed(() => {
      const pages = []
      const maxVisible = 5
      let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
      let end = Math.min(totalPages.value, start + maxVisible - 1)

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    })

    const fetchUsers = async () => {
      try {
        const response = await apiStore.get('/admin/users')
        users.value = response.data || []
      } catch (error) {
        console.error('Failed to fetch users:', error)
      } finally {
        loading.value = false
      }
    }

    const toggleAdmin = async (user) => {
      const action = user.is_admin ? 'remove admin privileges from' : 'make admin'
      if (confirm(`Are you sure you want to ${action} ${user.name}?`)) {
        submitting.value = true
        try {
          if (user.is_admin) {
            await apiStore.post(`/admin/users/${user.id}/remove-admin`)
          } else {
            await apiStore.post(`/admin/users/${user.id}/make-admin`)
          }
          await fetchUsers()
        } catch (error) {
          console.error('Failed to update user admin status:', error)
        } finally {
          submitting.value = false
        }
      }
    }

    const deleteUser = async (id) => {
      if (confirm('Are you sure you want to delete this user?')) {
        submitting.value = true
        try {
          await apiStore.delete(`/admin/users/${id}`)
          await fetchUsers()
        } catch (error) {
          console.error('Failed to delete user:', error)
        } finally {
          submitting.value = false
        }
      }
    }

    onMounted(() => {
      fetchUsers()
    })

    return {
      loading,
      users,
      submitting,
      searchQuery,
      currentPage,
      itemsPerPage,
      totalUsers,
      totalPages,
      paginatedUsers,
      visiblePages,
      toggleAdmin,
      deleteUser
    }
  }
}
</script>

<style scoped>
.admin-users {
  padding: 20px 0;
}

/* Modern Table Styles */
.orders-table-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.8);
  overflow: hidden;
}

.modern-orders-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-orders-table thead th {
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  color: #495057;
  font-weight: 600;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 20px 30px;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
}

.modern-orders-table tbody tr {
  transition: all 0.3s ease;
  border-bottom: 1px solid #f1f3f4;
}

.modern-orders-table tbody tr:hover {
  background: linear-gradient(135deg, #f8f9fa, #f1f3f4);
}

.modern-orders-table tbody td {
  padding: 20px 30px;
  vertical-align: middle;
}

.order-id {
  font-weight: 600;
  color: var(--theme, #6B8F71);
}

.customer-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.customer-avatar {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--theme, #6B8F71), #5a7d5f);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.customer-name {
  font-weight: 500;
  color: #2c3e50;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-block;
  text-align: center;
  min-width: 100px;
}

/* Role Badges */
.status-badge.role-admin {
  background: #ffebee;
  color: #c62828;
}

.status-badge.role-user {
  background: #e3f2fd;
  color: #1565c0;
}

/* Verification Badges */
.status-badge.verified {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-badge.unverified {
  background: #fff3e0;
  color: #ef6c00;
}

.action-btn {
  background: rgba(107, 143, 113, 0.1);
  color: var(--theme, #6B8F71);
  padding: 8px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
}

.action-btn:hover {
  background: var(--theme, #6B8F71);
  color: white;
  transform: translateY(-1px);
}

.action-btn.btn-danger {
  color: #EF6CC2;
  background: rgba(239, 108, 194, 0.1);
}

.action-btn.btn-danger:hover {
  background: #EF6CC2;
  color: white;
}

.filters-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.search-box {
  position: relative;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-box input {
  padding-left: 35px;
}

/* Pagination Styles */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  margin-top: 20px;
  border-top: 1px solid #dee2e6;
}

.pagination-info {
  color: #6c757d;
  font-size: 14px;
}

.pagination-controls {
  display: flex;
  gap: 8px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--theme, #6B8F71);
  color: white;
  border-color: var(--theme, #6B8F71);
}

.pagination-btn.active {
  background: var(--theme, #6B8F71);
  color: white;
  border-color: var(--theme, #6B8F71);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  border-radius: 0.2rem;
}
</style>